import React, { useState } from 'react';
import { Heart, ArrowLeft, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDemoLang } from './demoLang';

const GUIDES = [
  { id: 'jesus',    name: 'Jesus',     name_fr: 'Jésus',    tradition: 'Christianity',   tradition_fr: 'Christianisme', color: '#e2b04a', emoji: '✝️' },
  { id: 'moses',    name: 'Moses',     name_fr: 'Moïse',    tradition: 'Judaism',        tradition_fr: 'Judaïsme',      color: '#4a7db8', emoji: '✡️' },
  { id: 'muhammad', name: 'Muhammad',  name_fr: 'Mohammed', tradition: 'Islam',          tradition_fr: 'Islam',         color: '#2d8f5f', emoji: '☪️' },
  { id: 'buddha',   name: 'Buddha',    name_fr: 'Bouddha',  tradition: 'Buddhism',       tradition_fr: 'Bouddhisme',    color: '#c47a2a', emoji: '☸️' },
  { id: 'universal',name: 'Universal', name_fr: 'Universel', tradition: 'No preference', tradition_fr: 'Sans préférence', color: '#7d7d7d', emoji: '🌈' },
];

const TEACHINGS = {
  jesus: {
    fr: [
      "« Venez à moi, vous qui êtes fatigués — je vous donnerai le repos. » — Matthieu 11:28",
      "« Ne laissez pas votre cœur se troubler ; ayez foi. » — Jean 14:1",
      "« Vous êtes la lumière du monde. » — Matthieu 5:14",
    ],
    en: [
      "\"Come to me, all you who are weary — and I will give you rest.\" — Matthew 11:28",
      "\"Do not let your heart be troubled; have faith.\" — John 14:1",
      "\"You are the light of the world.\" — Matthew 5:14",
    ],
  },
  moses: {
    fr: [
      "« Sois fort et courageux — tu n'es pas seul. » — Deutéronome 31:6",
      "« L'Éternel te bénit et te garde. » — Nombres 6:24",
      "« Choisis la vie, afin que tu vives. » — Deutéronome 30:19",
    ],
    en: [
      "\"Be strong and courageous — you are not alone.\" — Deuteronomy 31:6",
      "\"The Lord bless you and keep you.\" — Numbers 6:24",
      "\"Choose life, so that you may live.\" — Deuteronomy 30:19",
    ],
  },
  muhammad: {
    fr: [
      "« Avec chaque difficulté vient une facilité. » — Coran 94:6",
      "« Le sourire à ton frère est une charité. » — Hadith",
      "« Cherche le savoir du berceau à la tombe. » — Hadith",
    ],
    en: [
      "\"With every hardship comes ease.\" — Qur'an 94:6",
      "\"Smiling at your brother is charity.\" — Hadith",
      "\"Seek knowledge from the cradle to the grave.\" — Hadith",
    ],
  },
  buddha: {
    fr: [
      "« Ce que nous pensons, nous le devenons. »",
      "« La paix vient de l'intérieur. Ne la cherche pas dehors. »",
      "« Sois gentil chaque fois que c'est possible. C'est toujours possible. »",
    ],
    en: [
      "\"What we think, we become.\"",
      "\"Peace comes from within. Do not seek it without.\"",
      "\"Be kind whenever possible. It is always possible.\"",
    ],
  },
  universal: {
    fr: [
      "« Respire lentement. Tu es en sécurité. »",
      "« Les émotions passent, comme les nuages dans le ciel. »",
      "« Tu es aimé — plus que tu ne l'imagines. »",
    ],
    en: [
      "\"Breathe slowly. You are safe.\"",
      "\"Emotions pass, like clouds across the sky.\"",
      "\"You are loved — more than you know.\"",
    ],
  },
};

const MOODS = [
  { key: 'happy', emoji: '😊', label_fr: 'Bien', label_en: 'Good' },
  { key: 'neutral', emoji: '😐', label_fr: 'Bof',  label_en: 'Meh' },
  { key: 'sad',   emoji: '😢', label_fr: 'Triste', label_en: 'Sad' },
];

export default function HealingWorlds() {
  const { lang, t } = useDemoLang();
  const [guide, setGuide] = useState(null);
  const [mood, setMood] = useState(null);
  const [showComfort, setShowComfort] = useState(false);

  const teachings = guide ? (lang === 'fr' ? TEACHINGS[guide.id].fr : TEACHINGS[guide.id].en) : [];

  const pickMood = (m) => {
    setMood(m);
    if (m.key === 'sad') setShowComfort(true);
  };

  return (
    <div className="space-y-5" data-testid="healing-page">
      {/* Intro — clearly separate space */}
      <section className="rounded-3xl bg-white/80 backdrop-blur p-5 border-2 border-emerald-100 shadow-squishy" data-testid="healing-intro">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-sky-100 flex items-center justify-center shrink-0"><Heart className="w-5 h-5 text-sky-500" /></div>
          <div>
            <h2 className="font-heading text-xl font-bold text-slate-800">
              {t("Mondes Apaisants — Compagnon Hôpital", "Healing Worlds — Hospital Companion")}
            </h2>
            <p className="text-slate-600 text-sm mt-1">
              {t("Ceci est un espace séparé et optionnel, pour le réconfort et l'inspiration. Il n'est jamais requis.",
                 "This is a separate, optional space for comfort and inspiration. It is never required.")}
            </p>
          </div>
        </div>
      </section>

      {/* Mood check-in */}
      <section className="rounded-3xl bg-white p-5 border-2 border-sky-100 shadow-squishy" data-testid="mood-checkin">
        <div className="font-heading font-bold text-slate-700 text-sm mb-3">
          {t("Comment te sens-tu ?", "How are you feeling?")}
        </div>
        <div className="flex gap-3 justify-center">
          {MOODS.map((m) => {
            const active = mood?.key === m.key;
            return (
              <button key={m.key} onClick={() => pickMood(m)}
                className={`w-20 h-20 rounded-3xl border-2 flex flex-col items-center justify-center transition-all ${active ? 'bg-sky-50 border-sky-400 scale-110 shadow-lg' : 'bg-white border-slate-200 hover:border-sky-300'}`}
                data-testid={`mood-${m.key}`}>
                <div className="text-4xl">{m.emoji}</div>
                <div className="text-slate-600 text-[11px] font-heading font-bold mt-1">{lang === 'fr' ? m.label_fr : m.label_en}</div>
              </button>
            );
          })}
        </div>
        {showComfort && (
          <div className="mt-4 p-3 rounded-2xl bg-sky-50 border border-sky-200 text-slate-700 text-sm" data-testid="comfort-offer">
            💙 {t("Veux-tu un message apaisant ? Choisis un guide ci-dessous — ou reviens à l'apprentissage.",
                 "Would you like a comforting message? Choose a guide below — or return to learning.")}
          </div>
        )}
      </section>

      {/* Spiritual guide selector (optional) */}
      <section className="rounded-3xl bg-white p-5 border-2 border-emerald-100 shadow-squishy" data-testid="guide-selector">
        <div className="font-heading font-bold text-slate-700 text-sm mb-1">
          {t("Guide spirituel (optionnel)", "Spiritual guide (optional)")}
        </div>
        <p className="text-slate-500 text-xs mb-3">
          {t("Complètement optionnel — jamais imposé. Tu peux fermer cet espace à tout moment.",
             "Completely optional — never pushed. You can leave this space at any time.")}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="guide-grid">
          {GUIDES.map((g) => {
            const active = guide?.id === g.id;
            return (
              <button key={g.id} onClick={() => setGuide(g)}
                className={`p-3 rounded-2xl border-2 text-left transition-all ${active ? 'shadow-lg scale-[1.02]' : 'bg-white border-slate-200 hover:border-emerald-300'}`}
                style={active ? { background: `${g.color}15`, borderColor: g.color } : {}}
                data-testid={`guide-${g.id}`}>
                <div className="text-2xl">{g.emoji}</div>
                <div className="font-heading font-bold text-slate-800 text-sm mt-1">{lang === 'fr' ? g.name_fr : g.name}</div>
                <div className="text-slate-500 text-[10px]">{lang === 'fr' ? g.tradition_fr : g.tradition}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Teachings */}
      {guide && (
        <section className="space-y-2" data-testid="teachings-list">
          <div className="text-slate-500 font-heading font-bold text-[10px] uppercase tracking-wider">
            {t("3 pensées choisies avec soin", "3 carefully chosen thoughts")} — {lang === 'fr' ? guide.name_fr : guide.name}
          </div>
          {teachings.map((tt, i) => (
            <div key={i} className="rounded-2xl bg-white p-4 border-2 shadow-squishy" style={{ borderColor: `${guide.color}55` }} data-testid={`teaching-${i}`}>
              <div className="flex items-start gap-3">
                <Sparkles className="w-4 h-4 shrink-0 mt-0.5" style={{ color: guide.color }} />
                <p className="text-slate-800 text-sm italic leading-relaxed">{tt}</p>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Return to learning — always visible */}
      <Link to="/dashboard"
        className="fixed bottom-4 right-4 z-30 h-12 px-5 rounded-full bg-white text-slate-800 border-2 border-sky-300 font-heading font-bold text-sm inline-flex items-center gap-2 shadow-lg hover:bg-sky-50 transition-colors"
        data-testid="return-to-learning">
        <ArrowLeft className="w-4 h-4" /> {t("Retour à l'apprentissage", "Return to learning")}
      </Link>
    </div>
  );
}
